<!-- Selection changes only the command band's left side. Page actions stay
     visible on the right because Export / Import still apply to the current
     filtered result rather than implicitly changing meaning. -->
<div class="dt-toolbar">
  <div class="dt-toolbar-primary" [class.br-flex-wrap]="!!selectionBinding" [class.br-gap-2]="!!selectionBinding">
    <div class="dt-filterbar" [class.dt-filterbar--hidden]="!selectionBinding && selection.count > 0">
      <mat-form-field *ngIf="searchable"
                      appearance="outline" class="dt-search" subscriptSizing="dynamic">
        <br-icon matPrefix>search</br-icon>
        <input matInput [placeholder]="searchPlaceholder" [formControl]="searchCtrl" />
        <button *ngIf="searchCtrl.value" type="button" mat-icon-button matSuffix
                (click)="searchCtrl.setValue('')" matTooltip="Clear search">
          <br-icon>close</br-icon>
        </button>
      </mat-form-field>
      <ng-content select="[dt-filters]"></ng-content>
    </div>

    <div class="dt-bulkbar" *ngIf="selection.count > 0">
      <span class="dt-bulk-count">{{ selection.count }} selected</span>
      <span class="dt-bulk-divider" aria-hidden="true"></span>
      <ng-content select="[dt-bulk-actions]"></ng-content>
      <button type="button" class="br-btn br-btn--link br-btn--sm dt-clear-selection"
              [disabled]="!selection.hasValue()" (click)="clearSelection()">{{ selectionBinding ? 'Clear loaded selection' : 'Clear selection' }}</button>
    </div>
  </div>

  <span class="dt-toolbar-spacer"></span>
  <div class="dt-toolbar-actions">
    <ng-content select="[dt-actions]"></ng-content>

    <span *ngIf="tableSettingsEnabled" class="dt-toolbar-divider" aria-hidden="true"></span>
    <button *ngIf="tableSettingsEnabled"
            type="button"
            mat-icon-button
            disableRipple
            class="dt-settings-button"
            [brPopoverFor]="tableSettingsMenu" brPopoverLabel="Table settings"
            (popoverClosed)="applyColumnSelection()"
            matTooltip="Table settings"
            aria-label="Table settings">
      <br-icon>settings</br-icon>
    </button>
  </div>
</div>


<!-- The popover template stays outside the toolbar so table content cannot interfere with
     its overlay lifecycle. -->
<ng-template #tableSettingsMenu let-popover="popover">
  <div class="dt-column-menu">
    <button *ngIf="columnPickerEnabled"
            type="button" class="br-btn br-btn--sm br-interactive dt-column-command"
            [disabled]="hiddenColumns.size === 0"
            (click)="showAllColumns(); popover.close()">
      <br-icon>visibility</br-icon> Unhide all
    </button>
    <!-- Picking columns is a batch edit; apply once when the popover closes. -->
    <div *ngIf="columnPickerEnabled"
         class="dt-column-menu-list">
      <mat-checkbox *ngFor="let column of columns"
                    [checked]="!hiddenColumns.has(column.id)"
                    [disabled]="!canHide(column.id)"
                    (change)="toggleColumn(column.id)">
        {{ column.label }}
      </mat-checkbox>
    </div>
    <button type="button" class="br-btn br-btn--sm br-interactive dt-column-command" *ngIf="resizableColumns"
            [disabled]="!hasCustomWidths" (click)="resetColumnWidths(); popover.close()">
      <br-icon>settings_ethernet</br-icon> Reset column widths
    </button>
  </div>
</ng-template>

<!-- Cell templates are queried as content children. Projecting the inert
     ng-template nodes keeps that page contract explicit without rendering them. -->
<ng-content select="ng-template[dtCell]"></ng-content>

<div class="dt-error" *ngIf="errorMessage && !loading" role="alert">
  <br-icon>error_outline</br-icon>
  <span>{{ errorMessage }}</span>
  <button type="button" class="br-btn br-btn--outline-danger br-btn--sm" (click)="retry()">Retry</button>
</div>

<div class="dt-scroll" #scrollBox [attr.data-dt-id]="instanceId">
  <!-- No `matSort` here: generated headers share the host-provided MatSort. -->
  <mat-table [dataSource]="dataSource" [trackBy]="trackByFn" [multiTemplateDataRows]="rowDetail != null"
             [@.disabled]="true" class="dt-table">
    <!-- Data columns share one header implementation. A column id is its
         default server sort key; pages opt out only for real exceptions. -->
    <ng-container *ngFor="let column of columns" [matColumnDef]="column.id">
      <mat-header-cell *matHeaderCellDef
                       [mat-sort-header]="sortKeyFor(column)"
                       [disabled]="!isSortable(column)"
                       [class.dt-col-end]="column.align === 'end'">
        {{ column.label }}
      </mat-header-cell>
      <mat-cell *matCellDef="let row" [class.dt-col-end]="column.align === 'end'">
        <ng-container *ngIf="cellTemplate(column.id) as template; else defaultCell"
                      [ngTemplateOutlet]="template"
                      [ngTemplateOutletContext]="{ $implicit: row }">
        </ng-container>
        <ng-template #defaultCell>{{ cellValue(row, column.id) }}</ng-template>
      </mat-cell>
    </ng-container>

    <!-- Leading checkbox column; rendered only when [selectable]. -->
    <ng-container matColumnDef="__select">
      <mat-header-cell *matHeaderCellDef class="dt-select-col">
        <mat-checkbox (change)="toggleAll()" [disabled]="loading" [checked]="isAllSelected()"
                      [indeterminate]="selection.hasValue() && !isAllSelected()"
                      aria-label="Select all on this page"></mat-checkbox>
      </mat-header-cell>
      <mat-cell *matCellDef="let row" class="dt-select-col">
        <mat-checkbox (change)="toggleRow(row)" [disabled]="loading" [checked]="selection.isSelected(row)"
                      aria-label="Select row"></mat-checkbox>
      </mat-cell>
    </ng-container>

    <!-- Optional hierarchy rail. Root rows page normally; children load on demand. -->
    <ng-container matColumnDef="__hierarchy">
      <mat-header-cell *matHeaderCellDef class="dt-hierarchy-col"></mat-header-cell>
      <mat-cell *matCellDef="let row" class="dt-hierarchy-col">
        <button *ngIf="canExpand(row)" type="button" mat-icon-button
                [attr.aria-label]="isExpanded(row) ? 'Collapse children' : 'Expand children'"
                [matTooltip]="isExpanded(row) ? 'Collapse children' : 'Expand children'"
                (click)="toggleChildren(row)">
          <br-icon>{{ isChildLoading(row) ? 'hourglass_empty' : (isExpanded(row) ? 'expand_more' : 'chevron_right') }}</br-icon>
        </button>
        <span *ngIf="isChildRow(row)" class="dt-child-guide" aria-hidden="true"></span>
      </mat-cell>
    </ng-container>

    <!-- One fixed trailing rail holds row actions. Table settings belong to the
         toolbar and do not create a data column. -->
    <!-- THE GRIP LIVES HERE, not in the page's rowActions template.
         It did live there, and it silently did nothing: cdkDragHandle finds
         its cdkDrag through DI, an embedded view resolves DI at its
         DECLARATION site, and the page declares that template where no
         cdkDrag is in scope. The token is `optional: true`, so there was no
         error to notice - the button rendered and the drag never started.
         Same template as the row, or no handle. -->
    <ng-container matColumnDef="__drag">
      <mat-header-cell *matHeaderCellDef class="dt-drag-col"></mat-header-cell>
      <mat-cell *matCellDef="let row" class="dt-drag-col">
        <button type="button" class="dt-drag-grip" cdkDragHandle
                matTooltip="Drag this row" aria-label="Drag this row">
          <br-icon>drag_indicator</br-icon>
        </button>
      </mat-cell>
    </ng-container>

    <ng-container matColumnDef="__actions">
      <mat-header-cell *matHeaderCellDef class="dt-actions-col">
        <button *ngIf="addLabel" type="button"
                class="br-btn br-btn--primary br-btn--sm dt-add-button" (click)="add.emit()">
          <br-icon>add</br-icon> {{ addLabel }}
        </button>
      </mat-header-cell>
      <mat-cell *matCellDef="let row" class="dt-actions-col">
        <button *ngIf="editable" type="button" mat-icon-button
                [matTooltip]="editTooltip" aria-label="Edit row"
                (click)="edit.emit(row)">
          <br-icon>edit</br-icon>
        </button>
        <ng-container *ngIf="rowActions"
                      [ngTemplateOutlet]="rowActions"
                      [ngTemplateOutletContext]="{ $implicit: row }"></ng-container>
        <button *ngIf="rowDetail" type="button" mat-icon-button
                [attr.aria-label]="isDetailOpen(row) ? 'Collapse details' : 'Expand details'"
                [attr.aria-expanded]="isDetailOpen(row)" (click)="toggleDetail(row)">
          <br-icon>{{ isDetailOpen(row) ? 'expand_less' : 'expand_more' }}</br-icon>
        </button>
      </mat-cell>
    </ng-container>

    <!-- Full-width detail under a row; the page's template renders only while that row is open. -->
    <ng-container matColumnDef="__detail">
      <mat-cell *matCellDef="let row" class="dt-detail-cell">
        <div *ngIf="isDetailOpen(row)" class="br-card dt-detail" role="region" aria-label="Row details">
          <ng-container [ngTemplateOutlet]="rowDetail" [ngTemplateOutletContext]="{ $implicit: row }"></ng-container>
        </div>
      </mat-cell>
    </ng-container>

    <mat-header-row *matHeaderRowDef="allColumns"></mat-header-row>
    <!-- A row is not a button. Making the whole row open a record swallows the
         drag that selects a cell's text, so copying an account number or an
         email became impossible; the page gives each row its own explicit ways
         in instead (a linked name, a View action). -->
    <mat-row *matRowDef="let row; columns: allColumns"
             [class.dt-child-row]="isChildRow(row)"
             [class.dt-row-selected]="selection.isSelected(row)"
             cdkDrag [cdkDragData]="row" [cdkDragDisabled]="!draggableRows"
             (cdkDragStarted)="rowDragStarted.emit()"
             (cdkDragEnded)="rowDragEnded.emit()"></mat-row>
    <mat-row *matRowDef="let row; columns: ['__detail']; when: hasDetailRow"
             class="dt-detail-row" [class.dt-detail-row--open]="isDetailOpen(row)"></mat-row>

    <tr class="mat-row dt-no-data" *matNoDataRow>
      <td class="mat-cell" [attr.colspan]="allColumns.length">
        <ng-container *ngIf="!loading && !errorMessage">{{ emptyText }}</ng-container>
      </td>
    </tr>
  </mat-table>
</div>

<!-- The footer band. Material's paginator reports the window ("1 – 25 of 240")
     but never the size of the set, so the total is stated here on the left and
     the paginator keeps the right. -->
<div class="dt-footer" *ngIf="paginated">
  <span class="dt-total">{{ total }} total</span>
  <mat-paginator [pageSize]="pageSize" [pageSizeOptions]="pageSizeOptions"
                 [length]="total"></mat-paginator>
</div>
